import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import RobotoRegularData from "../fonts/Roboto-Regular.ttf?inline";
import RobotoBoldData from "../fonts/Roboto-Bold.ttf?inline";
import { getScopeLabel, getScopeOrder, normalizeScope } from "../utils/treatmentScope";

const extractBase64 = (dataUrl) =>
  dataUrl && dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;

function registerRobotoFont(doc) {
  if (doc.__robotoRegistered) return;

  doc.addFileToVFS("Roboto-Regular.ttf", extractBase64(RobotoRegularData));
  doc.addFont("Roboto-Regular.ttf", "Roboto", "normal", "Identity-H");

  doc.addFileToVFS("Roboto-Bold.ttf", extractBase64(RobotoBoldData));
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bold", "Identity-H");

  doc.__robotoRegistered = true;
}

function renderConsentTable(doc, tableOptions) {
  const bottomMargin = tableOptions.bottomMargin ?? 20;
  autoTable(doc, {
    ...tableOptions,
    willDrawCell: (data) => {
      if (typeof tableOptions.willDrawCell === "function") {
        tableOptions.willDrawCell(data);
      }
      if (data.section === "body" && data.column.index === 0) {
        const pageHeight = doc.internal.pageSize.getHeight();
        const cellBottom = data.cell.y + data.cell.height;
        if (cellBottom > pageHeight - bottomMargin) {
          data.row.pageBreak = "before";
        }
      }
    },
  });
}


export const formatCurrencyValue = (value) =>
  Number(value || 0).toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const filterDoneServices = (services = []) =>
  (Array.isArray(services) ? services : []).filter((srv) => srv?.done);

export const calculateToothPayment = (services = [], payment = {}) => {
  const doneServices = filterDoneServices(services);
  const total = doneServices.reduce(
    (sum, srv) => sum + (Number(srv?.finalPrice) || 0),
    0
  );
  const hasValue = Boolean(payment?.hasValue);
  const paid = hasValue ? Number(payment?.amount) || 0 : 0;
  const diff = paid - total;

  let status = "none";
  let rest = 0;
  let advance = 0;

  if (total > 0) {
    if (Math.abs(diff) < 0.01) {
      status = "paid";
    } else if (diff > 0) {
      status = "advance";
      advance = diff;
    } else if (paid > 0) {
      status = "partial";
      rest = Math.abs(diff);
    } else {
      status = "unpaid";
      rest = total;
    }
  } else {
    status = "empty";
  }

  return {
    total,
    paid,
    rest,
    advance,
    status,
    hasValue,
  };
};

export const getPaymentStatusText = (summary) => {
  const { status, rest, advance } = summary;
  switch (status) {
    case "paid":
      return "Achitat integral";
    case "advance":
      return `Achitat integral + avans ${formatCurrencyValue(advance)} RON`;
    case "partial":
      return `Achitat parțial – rest ${formatCurrencyValue(rest)} RON`;
    case "unpaid":
      return `Rest de plată ${formatCurrencyValue(rest)} RON`;
    default:
      return "Fără servicii";
  }
};

export const summarizePlanPayments = (plan = []) => {
  const entries = (Array.isArray(plan) ? plan : []).map((t) => {
    const services = filterDoneServices(t?.services);
    const paymentSummary = calculateToothPayment(services, t?.payment);
    const scope = normalizeScope(t?.scope, t?.tooth);

    return {
      id: t?.id,
      tooth: t?.tooth,
      scope,
      label: getScopeLabel(scope, t?.tooth),
      services,
      paymentSummary,
      statusText: getPaymentStatusText(paymentSummary),
    };
  });

  entries.sort((a, b) => {
    const orderA = getScopeOrder(a.scope, a.tooth);
    const orderB = getScopeOrder(b.scope, b.tooth);
    if (orderA !== orderB) return orderA - orderB;
    if (a.scope === "tooth" && b.scope === "tooth") {
      const numA = Number(a.tooth);
      const numB = Number(b.tooth);
      if (Number.isFinite(numA) && Number.isFinite(numB) && numA !== numB) {
        return numA - numB;
      }
    }
    return 0;
  });

  const totalGeneral = entries.reduce((sum, e) => sum + e.paymentSummary.total, 0);
  const totalPaid = entries.reduce((sum, e) => sum + e.paymentSummary.paid, 0);
  const totalRemaining = entries.reduce((sum, e) => sum + e.paymentSummary.rest, 0);
  const totalAdvance = entries.reduce((sum, e) => sum + e.paymentSummary.advance, 0);

  return {
    entries,
    totalGeneral,
    totalPaid,
    totalRemaining,
    totalAdvance,
  };
};

export async function exportTratamentePDF(patient, plan, planSummary) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  registerRobotoFont(doc);

  doc.setFont("Roboto", "normal");
  doc.setFontSize(10);

  const marginLeft = 15;
  const marginRight = 15;
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - marginLeft - marginRight;

  const formatCurrency = (value) =>
    new Intl.NumberFormat("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(Number(value)) ? Number(value) : 0);

  const safe = (value, fallback = "-") => {
    if (value === null || value === undefined) return fallback;
    const trimmed = String(value).trim();
    return trimmed.length ? trimmed : fallback;
  };

  const now = new Date();
  const dateStr = now.toLocaleDateString("ro-RO");
  const timeStr = now.toLocaleTimeString("ro-RO", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const fullName = `${safe(patient?.lastName)} ${safe(patient?.firstName)}`.trim() || "-";
  const patientCity = safe(patient?.city || patient?.address);
  const patientCnp = safe(patient?.cnp);
  

  const isMinor = !!patient?.isMinor;
  const representative = patient?.legalRepresentative || {};
  const representativeName = isMinor
    ? `${safe(representative?.lastName)} ${safe(representative?.firstName)}`.trim() || "-"
    : "-";
  const representativeCity = isMinor ? safe(representative?.city) : "-";
  const representativeRelation = isMinor ? safe(representative?.relation) : "-";

 const planNotice = [
    "Pacientul înțelege că planul de tratament poate suferi modificări, în funcție de situația clinică constatată pe parcursul tratamentului. De asemenea, pacientul este informat că supraviețuirea pe termen lung a dintelui tratat depinde, în mare măsură, de cantitatea de țesut dentar dur rămas după tratamentul endodontic și că, în cazul dinților laterali (premolari și molari), este necesară o restaurare cu acoperire cuspidiană.",
    "Pacientul înțelege că, după tratamentul endodontic, pot apărea dureri la dintele tratat, acestea fiind de natură tranzitorie și putând fi gestionate cu analgezice precum ibuprofen sau paracetamol.",
    "Totodată, pacientul este informat că, în anumite cazuri, gingia din zona dintelui afectat se poate inflama, iar în asemenea situații poate fi necesară administrarea de antibiotice, acestea urmând a fi prescrise doar de către medicul endodont, după o evaluare clinică.",
  ].join("\n");

  // Header and titles
  doc.setFont("Roboto", "normal");
  doc.setFontSize(10);
  doc.text("ANEXA 1", marginLeft, 12);
  doc.text("(Anexa nr. 1 la normele metodologice)", pageWidth - marginRight, 12, {
    align: "right",
  });
  doc.text(
    "Formular de exprimare a acordului pacientului informat",
    pageWidth - marginRight,
    17,
    { align: "right" }
  );

  doc.setFont("Roboto", "bold");
  doc.setFontSize(16);
  doc.text("ACORDUL PACIENTULUI INFORMAT", pageWidth / 2, 48, { align: "center" });

  doc.setFont("Roboto", "normal");
  doc.setFontSize(9);
function printParagraph(doc, text, x, y, maxWidth, lineHeight, top, bottom) {
  const lines = doc.splitTextToSize(text, maxWidth);
  const pageH = doc.internal.pageSize.getHeight();
  let cy = y;
  const limit = pageH - bottom;

  for (const line of lines) {
    if (cy + lineHeight > limit) {
      doc.addPage();
      cy = top;
    }
    doc.text(line, x, cy);
    cy += lineHeight;
  }
  return cy;
}

// folosire:
doc.setFont("Roboto", "normal");
doc.setFontSize(10);
let cursorY = 60;

  // Section 1: Patient details (labels aligned)
  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginLeft, right: marginRight },
    theme: "grid",
    styles: { font: "Roboto", fontSize: 10, cellPadding: 3, valign: "middle" },
    columnStyles: {
      0: { cellWidth: 55 },
      1: { cellWidth: 55 },
      2: { cellWidth: contentWidth - 110 },
    },
    body: [
      [
        {
          content: "1. Datele pacientului",
          rowSpan: 5,
          styles: { fontStyle: "bold", valign: "top" },
        },
        { content: "Numele și prenumele:", styles: { font: "Roboto", fontSize: 9, fontStyle: "bold" } },
        fullName,
      ],
      [
        
        { content: "Domiciliul/Reședința:", styles: { font: "Roboto", fontSize: 9, fontStyle: "bold" }},
        patientCity,
      ],
      [
        
        { content: "CNP:", styles: { font: "Roboto", fontSize: 9, fontStyle: "bold" } },
        patientCnp,
      ],
   
    ],
  });

  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 6;

  // Section 2: Legal representative
  const representativeBody = [
    [
      {
        content: "2. Reprezentantul legal al pacientului(*)",
        rowSpan: 3,
        styles: { fontStyle: "bold", valign: "top" },
      },
      { content: "Numele, prenumele:", styles: { font: "Roboto", fontSize: 9, fontStyle: "bold" } },
      representativeName,
    ],
    [
      
      { content: "Domiciliul/Reședința:", styles: { font: "Roboto", fontSize: 9, fontStyle: "bold" } },
      representativeCity,
    ],
    [
      
      { content: "Calitatea:", styles: { font: "Roboto", fontSize: 9, fontStyle: "bold" } },
      representativeRelation,
    ],
  ];

  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginLeft, right: marginRight },
    theme: "grid",
    styles: { font: "Roboto", fontSize: 10, cellPadding: 3, valign: "middle" },
    columnStyles: {
      0: { cellWidth: 55 },
      1: { cellWidth: 55 },
      2: { cellWidth: contentWidth - 110 },
    },
    body: representativeBody,
  });

  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 6;

// Section 3: Act medical (descriere) - doar servicii, fără prețuri
const toothMap = new Map();
(Array.isArray(plan) ? plan : []).forEach((tooth) => {
  const scope = normalizeScope(tooth?.scope, tooth?.tooth);
  const label = getScopeLabel(scope, tooth?.tooth) || "Serviciu general";
  if (!toothMap.has(label)) {
    toothMap.set(label, []);
  }
  (tooth?.services || []).forEach((srv) => {
    toothMap.get(label).push({
      name: safe(srv?.name),
    });
  });
});

const planBody = [];
if (toothMap.size === 0) {
  planBody.push(["-", "Nu sunt înregistrate servicii pentru acest pacient."]);
} else {
  toothMap.forEach((services, label) => {
    if (services.length === 0) {
      planBody.push([label, "-"]);
      return;
    }
    services.forEach((service, index) => {
      const serviceCell = {
        content: `• ${service.name}`,
        styles: { font: "Roboto", fontSize: 9 },
      };
      if (index === 0) {
        planBody.push([
          { content: label, styles: { fontStyle: "bold" }, rowSpan: services.length },
          serviceCell,
        ]);
      } else {
        planBody.push([serviceCell]);
      }
    });
  });
}

autoTable(doc, {
  startY: cursorY,
  margin: { left: marginLeft, right: marginRight },
  theme: "grid",
  head: [["3. Actul medical", "Serviciu"]],
  styles: { font: "Roboto", fontSize: 10, cellPadding: 3, valign: "top" },
  headStyles: { fillColor: [255, 255, 255], textColor: 0, fontStyle: "bold" },
  columnStyles: {
    0: { cellWidth: 60, fontStyle: "bold" },
    1: { cellWidth: contentWidth - 60 },
  },
  body: planBody,
});

  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 6;

 

  

  // Section 4: Information checklist
  const infoLabelsFour = [
    "Date despre starea de sănătate",
    "Diagnostic",
    "Prognostic",
    "Natura și scopul actului medical propus",
    "Intervențiile și strategia terapeutică propuse",
    "Beneficiile și consecințele actului medical",
    "Riscurile potențiale ale actului medical",
    "Alternative viabile de tratament și riscurile acestora",
    "Riscurile neefectuării tratamentului",
    "Riscurile nerespectării recomandărilor medicale",
  ];

  renderConsentTable(doc, {
    startY: cursorY,
    margin: { left: marginLeft, right: marginRight },
    theme: "grid",
    head: [[
      "4. Au fost furnizate pacientului următoarele informații în legătură cu actul medical:",
      "Da",
      "Nu",
    ]],
    body: infoLabelsFour.map((label) => [label, "", ""]),
    styles: { font: "Roboto", fontSize: 9, cellPadding: 3, valign: "middle" },
    headStyles: { fillColor: [255, 255, 255], textColor: 0, font: "Roboto", fontSize: 9, fontStyle: "bold" },
    columnStyles: {
      0: {
        cellWidth: contentWidth - 40,
        font: "Roboto",
        fontSize: 9,
        overflow: "linebreak",
        halign: "left",
      },
      1: { cellWidth: 20, font: "Roboto", fontSize: 9, halign: "center" },
      2: { cellWidth: 20, font: "Roboto", fontSize: 9, halign: "center" },
    },
  });

  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 6;

  // Section 5: Consent for collection
  const infoLabelsFive = [
    "Pacientul este de acord cu recoltarea, păstrarea şi folosirea produselor biologice.",
  ];

  renderConsentTable(doc, {
    startY: cursorY,
    margin: { left: marginLeft, right: marginRight },
    theme: "grid",
    head: [[
      "5. Consimțământ pentru recoltare",
      "Da",
      "Nu",
    ]],
    body: infoLabelsFive.map((label) => [label, "", ""]),
    styles: { font: "Roboto", fontSize: 9, cellPadding: 3, valign: "middle" },
    headStyles: { fillColor: [255, 255, 255], textColor: 0, font: "Roboto", fontSize: 9, fontStyle: "bold" },
    columnStyles: {
      0: {
        cellWidth: contentWidth - 40,
        font: "Roboto",
        fontSize: 9,
        overflow: "linebreak",
        halign: "left",
      },
      1: { cellWidth: 20, font: "Roboto", fontSize: 9, halign: "center" },
      2: { cellWidth: 20, font: "Roboto", fontSize: 9, halign: "center" },
    },
  });

  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 6;


  // Section 6: Additional information
 
  const infoLabelsSix = [
     "Informații despre serviciile medicale disponibile",
   "Informații despre identitatea și statutul profesional al personalului care îl va trata(**)",
    "Informații despre regulile/practicile din unitatea medicală, pe care trebuie să le respecte",
    "Pacientul a fost încunoștințat că are dreptul la o a doua opinie medicală.",
  ];

  renderConsentTable(doc, {
    startY: cursorY,
    margin: { left: marginLeft, right: marginRight },
    theme: "grid",
    head: [[
      "6. Alte informații care au fost furnizate pacientului",
      "Da",
      "Nu",
    ]],
    body: infoLabelsSix.map((label) => [label, "", ""]),
    styles: { font: "Roboto", fontSize: 9, cellPadding: 3, valign: "middle" },
    headStyles: { fillColor: [255, 255, 255], textColor: 0, font: "Roboto", fontSize: 9, fontStyle: "bold" },
    columnStyles: {
      0: {
        cellWidth: contentWidth - 40,
        font: "Roboto",
        fontSize: 9,
        overflow: "linebreak",
        halign: "left",
      },
      1: { cellWidth: 20, font: "Roboto", fontSize: 9, halign: "center" },
      2: { cellWidth: 20, font: "Roboto", fontSize: 9, halign: "center" },
    },
  });

  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 6;

  // Section 7 notice

    const infoLabelsSeven = [
     "7. Pacientul dorește să fie informat în continuare despre starea sa de sănătate.",
  ];

   autoTable(doc, {
  startY: cursorY,
  margin: { left: marginLeft, right: marginRight },
  theme: "grid",
  head: [[
    "",
    "Da",
    "Nu",
  ]],
    body: infoLabelsSeven.map((label) => [label, "", ""]),
    styles: { fontStyle: "bold", font: "Roboto", fontSize: 9, cellPadding: 3, valign: "middle" },
    headStyles: { fillColor: [255, 255, 255], textColor: 0, font: "Roboto", fontSize: 9, fontStyle: "bold" },
    columnStyles: {
      0: {
        cellWidth: contentWidth - 40,
        font: "Roboto",
        fontSize: 9,
        overflow: "linebreak",
        halign: "left",
      },
      1: { cellWidth: 20, font: "Roboto", fontSize: 9, halign: "center" },
      2: { cellWidth: 20, font: "Roboto", fontSize: 9, halign: "center" },
    },
  });

  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 6;



  doc.setFont("Roboto", "normal");
  doc.setFontSize(8);
  doc.text(
    "*) Se utilizează în cazul minorilor și majorilor fără discernământ (pentru art. 8 alin. (3)-(5) din normele metodologice).",
    marginLeft,
    cursorY
  );
  cursorY += 4;
  doc.text(
    "**) Identificat în tabelul cu personalul medical care îngrijește pacientul.",
    marginLeft,
    cursorY
  );

  cursorY += 6;

  // Consent declaration (back to main content area)
  doc.setFont("Roboto", "normal");
  doc.setFontSize(10);
  const consentText = doc.splitTextToSize(
    `I) Subsemnatul, ${fullName}, declar că am înțeles toate informațiile furnizate de către Dr. Chiriță Andrei (numele și prenumele medicului/asistentului medical), și enumerate mai sus, că am prezentat medicului/asistentului medical doar informații adevărate și îmi exprim acordul informat pentru efectuarea actului medical.`,
    contentWidth
  );
  doc.text(consentText, marginLeft, cursorY);
  cursorY += consentText.length * 4 + 6;
  doc.text(
    `X ..........................................................   Data: ${dateStr}   Ora: ${timeStr}`,
    marginLeft,
    cursorY
  );
  cursorY += 4;
  doc.setFontSize(8);
  doc.text(
    "Semnătura pacientului/reprezentantului legal care consimte informat la efectuarea actului medical",
    marginLeft,
    cursorY
  );

  cursorY += 8;

  // Refusal declaration
  doc.setFont("Roboto", "normal");
  doc.setFontSize(10);
  const refusalSubject = isMinor ? representativeName : fullName;
  const refusalText = doc.splitTextToSize(
    `II) Subsemnatul pacient/Reprezentant legal, ${refusalSubject}, declar că am înțeles toate informațiile furnizate de către Dr. Chiriță Andrei (numele și prenumele medicului/asistentului medical care a informat pacientul) și enumerate mai sus, că mi s-au explicat consecințele refuzului actului medical și îmi exprim refuzul pentru efectuarea actului medical.`,
    contentWidth
  );
  doc.text(refusalText, marginLeft, cursorY);
  cursorY += refusalText.length * 4 + 6;
  doc.text(
    `X ..........................................................   Data: ${dateStr}   Ora: ${timeStr}`,
    marginLeft,
    cursorY
  );
  cursorY += 4;
  doc.setFontSize(8);
  doc.text(
    "Semnătura pacientului/reprezentantului legal care refuză efectuarea actului medical",
    marginLeft,
    cursorY
  );

  cursorY += 8;

  // Medical staff table
  const pageH = doc.internal.pageSize.getHeight();
  if (cursorY > pageH - 40) {
    doc.addPage();
    cursorY = 10;
  }

  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginLeft, right: marginRight },
    theme: "grid",
    styles: { font: "Roboto", fontSize: 10, cellPadding: 3, valign: "middle" },
    head: [["Nr. crt.", "Numele și prenumele", "Statutul profesional"]],
    body: [["1", "Chirita Andrei", "Medic specialist endodont"],
  ["", "", ""]],
    columnStyles: {
      0: { cellWidth: 20, halign: "center" },
      1: { cellWidth: 90 },
      2: { cellWidth: contentWidth - 110 },
    },
    headStyles: { fillColor: [255, 255, 255], textColor: 0, fontStyle: "bold" },
  });

  cursorY = (doc.lastAutoTable?.finalY || cursorY) + 10;

  // Plan notice (moved to end)
  doc.setFont("Roboto", "normal");
  doc.setFontSize(10);
  cursorY =
    printParagraph(
      doc,
      planNotice,
      marginLeft,
      cursorY,
      contentWidth,
      6,
      10,
      20
    ) + 6;

  // Footer: Date + Signature (bottom of last page)
  const footerY = doc.internal.pageSize.getHeight() - 15;
  doc.setFont("Roboto", "normal");
  doc.setFontSize(10);
  doc.text(`Data: ${dateStr}`, marginLeft, footerY);
  doc.text(
    "Semnătură: ____________________________",
    pageWidth - marginRight - 80,
    footerY
  );

  doc.save(`AcordPacient_${patient.lastName || ""}_${patient.firstName || ""}.pdf`);
}

export async function exportTratamenteQR(patient, plan, totalsOverride = null) {
  const summary =
    totalsOverride && typeof totalsOverride === "object" && "totalGeneral" in totalsOverride
      ? totalsOverride
      : summarizePlanPayments(plan);

  const data = {
    pacient: `${patient.lastName} ${patient.firstName}`,
    cnp: patient.cnp,
    phone: patient.phone,
    totals: {
      total: summary.totalGeneral,
      paid: summary.totalPaid,
      remaining: summary.totalRemaining,
      advance: summary.totalAdvance,
    },
    tratamente: summary.entries.map((entry) => ({
      tooth: entry.tooth,
      scope: entry.scope,
      label: entry.label || getScopeLabel(entry.scope, entry.tooth),
      services: entry.services.map((srv) => ({
        name: srv.name,
        finalPrice: srv.finalPrice,
      })),
      total: entry.paymentSummary.total,
      paid: entry.paymentSummary.paid,
      status: entry.statusText,
    })),
  };

  return await QRCode.toDataURL(JSON.stringify(data));
}

export function sendTratamenteEmail(patient, plan, totalsOverride = null) {
  const summary =
    totalsOverride && typeof totalsOverride === "object" && "totalGeneral" in totalsOverride
      ? totalsOverride
      : summarizePlanPayments(plan);

  const subject = encodeURIComponent(
    `Plan tratamente – ${patient.lastName || ""} ${patient.firstName || ""}`.trim()
  );

  const lines = [
    `Pacient: ${patient.lastName || ""} ${patient.firstName || ""}`.trim(),
    `CNP: ${patient.cnp || "-"}`,
    `Telefon: ${patient.phone || "-"}`,
    "",
    "Plan tratamente:",
  ];

  summary.entries.forEach((entry) => {
    lines.push(
      `- ${entry.label || getScopeLabel(entry.scope, entry.tooth) || "Zona"}: ${entry.statusText}`
    );
    entry.services.forEach((srv) => {
      lines.push(
        `    • ${srv.name} – ${formatCurrencyValue(srv.finalPrice ?? srv.basePrice ?? 0)} RON`
      );
    });
  });

  lines.push(
    "",
    `Total: ${formatCurrencyValue(summary.totalGeneral)} RON`,
    `Achitat: ${formatCurrencyValue(summary.totalPaid)} RON`,
    `Rest de plată: ${formatCurrencyValue(summary.totalRemaining)} RON`
  );
  if (summary.totalAdvance > 0) {
    lines.push(`Avans: ${formatCurrencyValue(summary.totalAdvance)} RON`);
  }

  const body = encodeURIComponent(lines.join("\n"));
  const email = patient.email || "";
  window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
}

export function exportProformaPDF(patient, plan, planSummary, options = {}) {
  const doc = new jsPDF();
  registerRobotoFont(doc);
  doc.setFont("Roboto", "normal");

  doc.setFontSize(16);
  doc.text("Plan de tratament", 14, 20);

  doc.setFontSize(11);
  doc.text(`Pacient: ${patient.lastName} ${patient.firstName}`, 14, 30);
  doc.text(`Data: ${new Date().toLocaleDateString()}`, 14, 36);

  const rows = [];
  (Array.isArray(plan) ? plan : []).forEach((tratament) => {
    const scope = normalizeScope(tratament?.scope, tratament?.tooth);
    const zoneLabel = getScopeLabel(scope, tratament?.tooth) || "-";
    (Array.isArray(tratament?.services) ? tratament.services : []).forEach((srv) => {
      rows.push([
        zoneLabel,
        srv?.name || "-",
        `${formatCurrencyValue(srv?.basePrice ?? 0)} RON`,
        `${Number(srv?.discount ?? 0)}%`,
        `${formatCurrencyValue(
          srv?.finalPrice ?? srv?.basePrice ?? 0
        )} RON`,
      ]);
    });
  });

  autoTable(doc, {
    head: [["Zona", "Serviciu", "Preț listă", "Discount", "Preț final"]],
    body: rows,
    startY: 48,
    styles: { font: "Roboto", fontSize: 10, cellPadding: 3, halign: "left" },
    theme: "grid",
    tableWidth: "wrap", 
    headStyles: {
      font: "Roboto",
      fontSize: 10,
      fontStyle: "bold",
      fillColor: [255, 255, 255],
      textColor: 0,
    },
    columnStyles: {
      0: { cellWidth: 25, halign: "left" },
      1: { cellWidth: 70, halign: "left" },
      2: { cellWidth: 30, halign: "left" },
      3: { cellWidth: 25, halign: "left" },
      4: { cellWidth: 30, halign: "left" },
    },
  });

  const summary =
    planSummary && typeof planSummary === "object"
      ? planSummary
      : summarizePlanPayments(plan);

  const totalPlan = (Array.isArray(plan) ? plan : []).reduce((sum, tratament) => {
    const treatmentTotal = (Array.isArray(tratament?.services) ? tratament.services : []).reduce(
      (serviceSum, srv) =>
        serviceSum + Number(srv?.finalPrice ?? srv?.basePrice ?? 0),
      0
    );
    return sum + treatmentTotal;
  }, 0);
  const totalPaid = Number(summary.totalPaid || 0);
  const remaining = Math.max(totalPlan - totalPaid, 0);

  const finalY = doc.lastAutoTable?.finalY || 58;
  doc.text(`Total: ${formatCurrencyValue(totalPlan)} RON`, 14, finalY + 10);
  doc.text(`Total achitat: ${formatCurrencyValue(totalPaid)} RON`, 14, finalY + 17);
  doc.text(`Rest de plată: ${formatCurrencyValue(remaining)} RON`, 14, finalY + 24);
  doc.text("Semnătură pacient: ____________________", 14, finalY + 35);

  const safeLastName = (patient.lastName || "").replace(/\s+/g, "");
  const safeFirstName = (patient.firstName || "").replace(/\s+/g, "");
  const fileName = `PlanTratament_${safeLastName}_${safeFirstName}.pdf`;
  const dataUri = doc.output("datauristring");

  if (!options?.skipDownload) {
    doc.save(fileName);
  }

  if (typeof options?.onGenerated === "function") {
    options.onGenerated({ dataUri, fileName });
  }

  return { dataUri, fileName };
}
